/**
 * TopBar — верхняя панель (.topbar) внутри .maincol.
 *
 * Слева: бургер (мобайл) + заголовок/подзаголовок раздела.
 * Справа: actions страницы + переключатель темы + меню пользователя.
 *
 * Бренд продукта живёт в сайдбаре (.rail__brand), здесь — только заголовок
 * текущего раздела (контракт как у support AppShell).
 */

import type { ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Avatar, Icon, IconButton, Tooltip } from '@/shared/ui';
import { useAuth } from '@/auth/useAuth';
import { useTheme } from '@/shared/theme/ThemeContext';

const ROLE_LABEL: Record<string, string> = {
  admin: 'Администратор',
  curator: 'Куратор',
};

function ThemeToggle() {
  const { theme, toggle } = useTheme();
  const dark = theme === 'dark';
  return (
    <Tooltip label={dark ? 'Светлая тема' : 'Тёмная тема'}>
      <button className="iconbtn" onClick={toggle} aria-label="Переключить тему">
        <Icon name={dark ? 'Moon' : 'Sun'} />
      </button>
    </Tooltip>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;

  const handleLogout = async () => {
    await logout();
    await navigate({ to: '/login' });
  };

  return (
    <div className="row center gap10">
      <span className="usermenu-pill row center gap10">
        <Avatar name={user.display_name} size={30} />
        <span className="usermenu-info col" style={{ alignItems: 'flex-start', gap: 0 }}>
          <span style={{ fontSize: 12.5, fontWeight: 800, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
            {user.display_name}
          </span>
          <span className="muted" style={{ fontSize: 10.5, fontWeight: 700 }}>
            {ROLE_LABEL[user.role] ?? user.role}
          </span>
        </span>
      </span>
      <Tooltip label="Выйти">
        <button className="iconbtn" aria-label="Выйти" onClick={() => void handleLogout()}>
          <Icon name="LogOut" />
        </button>
      </Tooltip>
    </div>
  );
}

export function TopBar({
  title,
  subtitle,
  actions,
  navOpen,
  onToggleNav,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  navOpen: boolean;
  onToggleNav: () => void;
}) {
  return (
    <header className="topbar">
      <IconButton
        icon={navOpen ? 'X' : 'Menu'}
        className="rail-toggle"
        aria-label={navOpen ? 'Закрыть меню' : 'Открыть меню'}
        aria-expanded={navOpen}
        onClick={onToggleNav}
      />
      <div className="col" style={{ gap: 1, minWidth: 0 }}>
        <div className="h2" style={{ fontSize: 19 }}>
          {title}
        </div>
        {subtitle && (
          <div className="muted" style={{ fontSize: 12.5 }}>
            {subtitle}
          </div>
        )}
      </div>
      <div className="grow" />
      {actions}
      <komma-switcher current="ads" />
      <ThemeToggle />
      <UserMenu />
    </header>
  );
}
